/**
 * Fleet membership resolution.
 *
 * Dependency-free (no Meteor imports) so it can be unit tested and used from
 * the bot process, the server methods and the UI alike. The collection and
 * method declarations live in fleets.ts, which re-exports this.
 */

/** The shape resolution needs; the real Fleet doc has more. */
export interface FleetLike {
  _id: string;
  prefix?: string;
  /** Usernames excluded from this fleet (case-insensitive). An excluded bot
   *  falls through to the next-longest matching fleet, or to no fleet --
   *  the same composability rule as the prefixes themselves. */
  excludedBots?: string[];
}

/**
 * The fleet a username belongs to: the LONGEST matching prefix wins.
 *
 * Longest-match is what makes prefixes composable. With fleets `MogBot` and
 * `MogBotsSelling`, the sell bot resolves to its own fleet while every other
 * MogBot* stays in the general one, so a special case can be carved out later
 * without rewriting the original rule.
 *
 * Matching is case-insensitive, and a blank prefix never matches -- an empty
 * string is a prefix of every username, so without that guard one blank row
 * would silently swallow the entire fleet.
 */
export const resolveFleet = <T extends FleetLike>(
  username: string | undefined | null,
  fleets: T[],
): T | null => {
  if (!username) return null;
  const name = username.toLowerCase();
  let best: T | null = null;
  let bestLen = 0;
  for (const f of fleets) {
    const prefix = (f.prefix || '').trim().toLowerCase();
    if (!prefix || !name.startsWith(prefix)) continue;
    if (f.excludedBots?.some((b) => b.trim().toLowerCase() === name)) continue;
    if (prefix.length > bestLen) { best = f; bestLen = prefix.length; }
  }
  return best;
};

/** A Mongo query object that actually constrains something. */
export type CardQuery = Record<string, unknown>;

const usableQuery = (q: unknown): CardQuery | null => (
  q && typeof q === 'object' && !Array.isArray(q) && Object.keys(q as object).length
    ? q as CardQuery : null
);

const usableCap = (n: unknown): number | null => (
  typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null
);

export interface TradeRuleSources {
  /** Per-bot override: complete replacement, never merged (MOGBO-12). */
  botQuery?: unknown;
  fleetQuery?: unknown; fleetCap?: unknown;
  settingsQuery?: unknown; settingsCap?: unknown;
}

export interface TradeRules {
  /** null = no query configured anywhere -> caller falls back to the legacy
   *  target collections, so an unconfigured system trades exactly as before. */
  query: CardQuery | null;
  /** null = no cap configured -> caller applies its own default. */
  cap: number | null;
}

/**
 * Effective buy/sell rules: fleet-level beats Settings, independently per
 * field -- a fleet may override just the cap while inheriting the global query.
 *
 * Blank/invalid values are "absent" at every level (the same convention as the
 * message overrides), so clearing a field in the admin always falls through
 * rather than storing a rule that matches nothing.
 */
export const resolveTradeRules = (src: TradeRuleSources): TradeRules => ({
  query: usableQuery(src.botQuery) ?? usableQuery(src.fleetQuery) ?? usableQuery(src.settingsQuery),
  cap: usableCap(src.fleetCap) ?? usableCap(src.settingsCap),
});

/**
 * Replenish priority below the bot-level override: fleet, then Settings, then
 * the platform default. Higher number = replenishes first. The bot.config
 * override is applied by the caller (it lives on the bot doc, not here).
 */
export const resolveReplenishPriority = (
  fleetPriority: unknown,
  settingsPriority: unknown,
  fallback: number,
): number => {
  const num = (n: unknown): number | null => (
    typeof n === 'number' && Number.isFinite(n) ? n : null
  );
  return num(fleetPriority) ?? num(settingsPriority) ?? fallback;
};
